import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Bot, 
  Settings, 
  CheckCircle, 
  AlertCircle,
  Copy,
  ExternalLink,
  RefreshCw,
  Users,
  ShoppingCart,
  ClipboardList,
  ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TelegramBotSetup() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [botToken, setBotToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [botInfo, setBotInfo] = useState(null);
  const [groups, setGroups] = useState([]);
  const [savingToken, setSavingToken] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchBotInfo();
    fetchGroups();
  }, []);

  const fetchBotInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/telegram/bot-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data.error) {
        setBotInfo(response.data.result);
      }
    } catch (error) {
      console.log('Bot not configured yet');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API}/api/telegram/groups/${userData.company_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data || []);
    } catch (error) {
      console.log('No groups yet');
    }
  };

  const setWebhook = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API}/api/telegram/webhook`;
      const response = await axios.post(`${API}/api/telegram/set-webhook?webhook_url=${encodeURIComponent(url)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.ok) {
        toast.success('Webhook configured successfully!');
        setWebhookUrl(url);
      } else {
        toast.error('Failed to set webhook');
      }
    } catch (error) {
      toast.error('Error setting webhook');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Bot className="w-7 h-7 text-indigo-600" />
              Telegram Job Bot
            </h1>
            <p className="text-slate-500">Set up your smart job tracking assistant</p>
          </div>
        </div>

        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {botInfo ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              )}
              Bot Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {botInfo ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">@{botInfo.username}</p>
                    <p className="text-sm text-slate-500">{botInfo.first_name}</p>
                  </div>
                  <Badge className="ml-auto bg-green-100 text-green-700">Connected</Badge>
                </div>
                <Button onClick={setWebhook} variant="outline" className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Webhook
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 font-medium">Bot token not configured</p>
                  <p className="text-sm text-amber-600 mt-1">
                    Contact your administrator to set up the Telegram bot token in the environment.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Setup Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Setup Instructions</CardTitle>
            <CardDescription>Follow these steps to connect your team's Telegram group</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                <div>
                  <p className="font-medium">Create a Telegram Bot</p>
                  <p className="text-sm text-slate-500">Message @BotFather on Telegram and use /newbot command</p>
                  <Button variant="link" className="p-0 h-auto text-indigo-600" onClick={() => window.open('https://t.me/BotFather', '_blank')}>
                    Open BotFather <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                <div>
                  <p className="font-medium">Add Bot to Group</p>
                  <p className="text-sm text-slate-500">Add your new bot to your company's Telegram group</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                <div>
                  <p className="font-medium">Register the Group</p>
                  <p className="text-sm text-slate-500">In your Telegram group, send:</p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="bg-slate-200 px-2 py-1 rounded text-sm">/register {user?.company_id || 'YOUR_COMPANY_ID'}</code>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`/register ${user?.company_id || ''}`)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                <div>
                  <p className="font-medium">Start Using!</p>
                  <p className="text-sm text-slate-500">Your team can now record jobs by just chatting!</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Connected Groups */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Connected Groups
            </CardTitle>
          </CardHeader>
          <CardContent>
            {groups.length > 0 ? (
              <div className="space-y-2">
                {groups.map((group) => (
                  <div key={group.group_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="font-medium">{group.group_name || 'Unnamed Group'}</p>
                        <p className="text-xs text-slate-500">ID: {group.group_id}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No groups connected yet</p>
                <p className="text-sm">Follow the setup instructions above</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>What Your Bot Can Do</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <ClipboardList className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Job Recording</p>
                  <p className="text-sm text-blue-700">"Fixed the fence in paddock 3"</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <MessageCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-900">Smart Queries</p>
                  <p className="text-sm text-purple-700">"What did John do last week?"</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Shopping List</p>
                  <p className="text-sm text-green-700">"Add feed pellets to list"</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                <Users className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Roster Queries</p>
                  <p className="text-sm text-amber-700">"Who worked yesterday?"</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
